import { css } from '@emotion/css';
import styled from '@emotion/styled';
import { TextField, Typography } from '@material-ui/core';
import { validate as isValidEmail } from 'email-validator';
import { useState } from 'react';
import ReactHelmet from 'react-helmet';
import { ButtonLoader } from '../../../../components/ButtonWithPromise';
import { Form } from '../../../../components/Form';
import { FormField } from '../../../../components/Form/FormField';
import { FormSubmitButton } from '../../../../components/Form/FormSubmitButton';
import { InfoBox } from '../../../../components/InfoBox';
import { InfoCircle } from '../../../../components/InfoCircle';
import { useUnsafeTranslation } from '../../../../i18next';
import { FlexContainer } from '../../../../layout/Flex';
import { TopNavBarLogoOnly } from '../../../../layout/TopNav';
import { lightMdToJsx } from '../../../../services/formatter';
import { Img } from '../../../Users/pages/Imports/components/sources/common';
import { registerAdminUser } from '../../services';

export const SignUpWrapper = styled('div')`
  height: 100vh;
  width: 100%;
`;

export const SignUpContainer = styled('div')((p) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  padding: p.theme.spacing(2),
  gap: p.theme.spacing(2),
  border: p.theme.custom.border.standard,
  borderRadius: p.theme.custom.border.radius(),
  width: '40%',
  maxWidth: '40%'
}));

type SignUpValues = {
  firstName: string;
  lastName: string;
  workEmail: string;
};

const SignUpForm = ({
  emailError,
  onSubmit
}: {
  onSubmit: (values: SignUpValues) => Promise<void>;
  emailError: string;
}) => {
  const { t } = useUnsafeTranslation();
  const initialValues: SignUpValues = {
    firstName: '',
    lastName: '',
    workEmail: ''
  };
  return (
    <SignUpContainer>
      <Typography component="h3" variant="h3">
        {t('Sign up now to get free access to Moments')}
      </Typography>
      <Form<SignUpValues>
        initialVales={initialValues}
        onSubmit={onSubmit}
        className={css({ width: '100%' })}
      >
        {({ submitting, values }) => {
          return (
            <FlexContainer
              fullWidth
              spacing={2}
              direction="column"
              alignItems="flex-start"
            >
              <FlexContainer fullWidth>
                <FormField name="firstName">
                  {({ input }) => (
                    <TextField
                      {...input}
                      label={t('First Name')}
                      variant="outlined"
                      fullWidth
                    />
                  )}
                </FormField>
                <FormField name="lastName">
                  {({ input }) => (
                    <TextField
                      {...input}
                      label={t('Last Name')}
                      variant="outlined"
                      fullWidth
                    />
                  )}
                </FormField>
              </FlexContainer>
              <FlexContainer fullWidth>
                <FormField name="workEmail">
                  {({ input }) => (
                    <TextField
                      {...input}
                      variant="outlined"
                      label={t('Email address')}
                      type="email"
                      error={!!emailError}
                      helperText={emailError}
                      fullWidth
                    />
                  )}
                </FormField>
              </FlexContainer>
              {isValidEmail(values.workEmail) && (
                <InfoBox color="blue">
                  <FlexContainer>
                    <InfoCircle size={20} />
                    <Typography component="h6" variant="h6">
                      {t(
                        `You will only be able to send emails to {{email}} addresses`,
                        {
                          email: (values.workEmail.split('@')[1] || '').trim()
                        }
                      )}
                    </Typography>
                  </FlexContainer>
                  {t(
                    `To prevent spam, Dado Moments is restricted to sending emails to the email domain you provide here. Please ensure you’ve entered the correct work email address.`
                  )}
                </InfoBox>
              )}
              <FormSubmitButton
                submitting={submitting}
                submitComponent={
                  <>
                    <ButtonLoader /> {t('Signing up...')}
                  </>
                }
                disabled={
                  !values.firstName || !values.lastName || !values.workEmail
                }
              >
                {t('Sign up')}
              </FormSubmitButton>
            </FlexContainer>
          );
        }}
      </Form>
    </SignUpContainer>
  );
};

const SignUpConfirmation = () => {
  const { t } = useUnsafeTranslation();
  return (
    <FlexContainer direction="column" spacing={2}>
      <Typography component="h3" variant="h3">
        {t('Check your email to complete your sign-up')}
      </Typography>
      <Typography>
        {lightMdToJsx(
          t(
            'Look for an email from **notifications@dadohr.com**. The final step takes less than 2 minutes.'
          )
        )}
      </Typography>
    </FlexContainer>
  );
};

export const SignUpOverview = () => {
  const { t } = useUnsafeTranslation();
  const [formState, setFormState] = useState({
    success: false,
    workEmailError: ''
  });

  const onSubmit = async (values: SignUpValues) => {
    const emailValidationResult = await registerAdminUser({
      email: values.workEmail,
      firstName: values.firstName,
      lastName: values.lastName
    });

    if (emailValidationResult.error) {
      if (emailValidationResult.error === 'ADMIN_EMAIL_TAKEN') {
        setFormState({
          success: false,
          workEmailError: t('Email address already exists')
        });
      }
      if (emailValidationResult.error === 'ADMIN_EMAIL_INVALID') {
        setFormState({
          success: false,
          workEmailError: t('Please use a valid email address')
        });
      }
      if (emailValidationResult.error === 'ADMIN_EMAIL_BLOCKED') {
        setFormState({
          success: false,
          workEmailError: t('Please use your work email address')
        });
      }
      return;
    }
    setFormState({
      workEmailError: '',
      success: true
    });
  };

  return (
    <SignUpWrapper>
      <ReactHelmet>
        <title>Dado Moments | Sign up</title>
      </ReactHelmet>
      <TopNavBarLogoOnly />
      <FlexContainer direction="column" marginTop={4} spacing={4}>
        {!formState.success && (
          <SignUpForm
            emailError={formState.workEmailError}
            onSubmit={onSubmit}
          />
        )}
        {formState.success && <SignUpConfirmation />}
        <Img src="/images/sign-up/signUp.svg" maxHeight={300} />
      </FlexContainer>
    </SignUpWrapper>
  );
};
